(function() {
  'use strict';

  var RAW_PROPS = [
    'startMonth',
    'startYear',
    'weekBeginsOn',
    'eventList',
    'projectName',
    'type',
    'appleProjectId',
    'projectSource',
    'shareProjectId',
    'userId',
    'userid',
    'creationDate'
  ];

  function getSource() {
    if (typeof window.getSource === 'function') return window.getSource();
    if (window.Capacitor || window.cordova) return 'ios';
    return 'web';
  }

  function normalizeSource(source) {
    var raw = (source === undefined || source === null) ? '' : String(source);
    var normalized = raw.trim().toLowerCase();

    if (!normalized) {
      return (window.Capacitor || window.cordova) ? 'ios' : 'web';
    }

    if (normalized === 'web') return 'web';
    if (normalized === 'ios') return 'ios';

    if (
      normalized.indexOf('ios') !== -1 ||
      normalized.indexOf('capacitor') !== -1 ||
      normalized.indexOf('cordova') !== -1
    ) {
      return 'ios';
    }

    if (
      normalized.indexOf('web') !== -1 ||
      normalized.indexOf('browser') !== -1
    ) {
      return 'web';
    }

    return normalized;
  }

  // Fail-open wrapper: resolves with the promise's value, or with
  // `fallbackValue` once `timeoutMs` elapses. Used on every path that gates
  // checkout on normalization, so a request that hangs (captive portal, proxy,
  // mobile handoff) degrades to an un-normalized checkout instead of a dead
  // button. It never rejects.
  function withFailOpenTimeout(promise, timeoutMs, label, fallbackValue) {
    if (!promise || typeof promise.then !== 'function') {
      return Promise.resolve(fallbackValue);
    }

    return new Promise(function(resolve) {
      var settled = false;
      var timer = window.setTimeout(function() {
        if (settled) return;
        settled = true;
        console.warn('[Cart Normalization] ' + label + ' timed out after ' + timeoutMs + 'ms; continuing.');
        resolve(fallbackValue);
      }, timeoutMs);

      promise
        .then(function(value) {
          if (settled) return;
          settled = true;
          window.clearTimeout(timer);
          resolve(value);
        })
        .catch(function(error) {
          if (settled) return;
          settled = true;
          window.clearTimeout(timer);
          console.error('[Cart Normalization] ' + label + ' failed:', error);
          resolve(fallbackValue);
        });
    });
  }

  var CHECKOUT_NORMALIZATION_TIMEOUT_MS = 8000;

  function shouldRun() {
    var source = normalizeSource(getSource());
    return source === 'web' || source === 'ios';
  }

  function isCartPage() {
    var path = window.location.pathname || '';
    return /\/cart(\/|$)/.test(path);
  }

  // Opt-in only. This dumps cart contents (including _projectId / _userId) to
  // the console and issues an extra /cart.js request; it fired two or three
  // times per checkout click on real orders. Set window.__mimeoCartDebug = true
  // in the console to turn it back on while debugging.
  function logCheckoutPayloadDebug(contextLabel, form) {
    if (window.__mimeoCartDebug !== true) return;

    try {
      var formAction = form && form.getAttribute ? form.getAttribute('action') : '';
      var quantityInputs = form ? form.querySelectorAll("input[name='updates[]']") : [];
      var quantitySummary = Array.prototype.map.call(quantityInputs, function(input) {
        return {
          line: input && input.dataset ? input.dataset.index : undefined,
          value: input ? input.value : undefined,
          min: input ? input.min : undefined,
          max: input ? input.max : undefined,
          step: input ? input.step : undefined
        };
      });

      console.group('[Checkout Debug] ' + contextLabel);
      console.error('[Checkout Debug] form.action:', formAction);
      console.error('[Checkout Debug] form.method:', form && form.method ? form.method : 'post');
      console.error('[Checkout Debug] location.pathname:', window.location.pathname);
      console.error('[Checkout Debug] nativeQuantityMode:', window.__mimeoUseNativeCartQuantity === true);
      console.error('[Checkout Debug] quantityInputs:', quantitySummary);

      fetch('/cart.js', { method: 'GET', credentials: 'same-origin' })
        .then(function(response) {
          if (!response.ok) {
            throw new Error('Failed to fetch /cart.js for debug: ' + response.status);
          }
          return response.json();
        })
        .then(function(cart) {
          var items = Array.isArray(cart && cart.items) ? cart.items : [];
          console.error('[Checkout Debug] cart.item_count:', cart && cart.item_count);
          console.error('[Checkout Debug] cart.total_price:', cart && cart.total_price);
          console.error('[Checkout Debug] cart.items:', items.map(function(item) {
            var props = item && item.properties ? item.properties : {};
            return {
              key: item && item.key,
              title: (item && (item.product_title || item.title)) || '',
              quantity: item && item.quantity,
              sku: item && item.sku,
              projectId: props._projectId || props.projectId || '',
              moduleType: props._moduleType || props.moduleType || '',
              isMainPart: props._isMainPart || props.isMainPart || ''
            };
          }));
        })
        .catch(function(error) {
          console.error('[Checkout Debug] cart snapshot error:', error);
        })
        .finally(function() {
          console.groupEnd();
        });
    } catch (error) {
      console.error('[Checkout Debug] logger failed:', error);
    }
  }

  function isCheckoutPage() {
    var path = window.location.pathname || '';
    return /\/checkouts\//.test(path);
  }

  function isCartOrCheckoutPage() {
    return isCartPage() || isCheckoutPage();
  }

  function hasRawProperties(properties) {
    if (!properties || typeof properties !== 'object') return false;
    return RAW_PROPS.some(function(key) { return Object.prototype.hasOwnProperty.call(properties, key); });
  }

  function runSerializedCartMutation(mutationCallback) {
    if (typeof mutationCallback !== 'function') {
      return Promise.reject(new Error('Cart mutation callback must be a function'));
    }

    if (typeof window.__mimeoRunCartMutation === 'function') {
      return window.__mimeoRunCartMutation(mutationCallback);
    }

    return Promise.resolve().then(mutationCallback);
  }

  function decodeBase64Url(value) {
    if (!value) return '';

    var normalized = String(value).replace(/-/g, '+').replace(/_/g, '/');
    while (normalized.length % 4 !== 0) {
      normalized += '=';
    }

    try {
      return atob(normalized);
    } catch (error) {
      return '';
    }
  }

  function parseJwtPayload(token) {
    if (!token || typeof token !== 'string') return null;

    var parts = token.split('.');
    if (parts.length < 2) return null;

    var payloadText = decodeBase64Url(parts[1]);
    if (!payloadText) return null;

    try {
      return JSON.parse(payloadText);
    } catch (error) {
      return null;
    }
  }

  function normalizeIdentityValue(value) {
    if (value === undefined || value === null) return '';
    var normalized = String(value).trim();
    return normalized ? normalized.toLowerCase() : '';
  }

  function getCurrentUserIdentitySet() {
    var identities = {};

    function addIdentity(value) {
      var normalized = normalizeIdentityValue(value);
      if (!normalized) return;
      identities[normalized] = true;
    }

    var shopifyCustomer = window.shopifyCustomer || {};
    addIdentity(shopifyCustomer.customerId);
    addIdentity(shopifyCustomer.email);

    var authToken = null;
    try {
      if (window.authState && typeof window.authState.getTokenSync === 'function') {
        authToken = window.authState.getTokenSync() || null;
      }

      if (!authToken) {
        authToken = localStorage.getItem('auth_token');
      }
    } catch (error) {
      authToken = null;
    }

    var tokenClaims = parseJwtPayload(authToken);
    if (tokenClaims && typeof tokenClaims === 'object') {
      [
        'userId',
        'userid',
        'sub',
        'uid',
        'id',
        'customerId',
        'email'
      ].forEach(function(claimKey) {
        addIdentity(tokenClaims[claimKey]);
      });
    }

    return identities;
  }

  function getShopifyCustomerId() {
    var customer = window.shopifyCustomer || {};
    return customer.customerId ? String(customer.customerId) : '';
  }

  function normalizeProjectId(value) {
    if (value === undefined || value === null) return '';
    var normalized = String(value).trim();
    return normalized ? normalized.toLowerCase() : '';
  }

  function collectProjectIdentifierSet(project) {
    var identifiers = {};
    if (!project || typeof project !== 'object') return identifiers;

    [
      project.id,
      project.projectId,
      project.projectID,
      project.guid,
      project.projectGuid
    ].forEach(function(candidate) {
      var normalized = normalizeProjectId(candidate);
      if (normalized) {
        identifiers[normalized] = true;
      }
    });

    return identifiers;
  }

  function delay(ms) {
    return new Promise(function(resolve) {
      window.setTimeout(resolve, ms);
    });
  }

  function getAuthTokenForProjectValidation() {
    var maxAttempts = 5;
    var retryDelayMs = 250;

    function readTokenOnce() {
      if (window.authState && typeof window.authState.getTokenSync === 'function') {
        try {
          var syncToken = window.authState.getTokenSync();
          if (syncToken) return Promise.resolve(syncToken);
        } catch (error) {}
      }

      if (window.authState && typeof window.authState.getToken === 'function') {
        return Promise.resolve()
          .then(function() {
            return window.authState.getToken();
          })
          .catch(function() {
            return null;
          });
      }

      try {
        return Promise.resolve(localStorage.getItem('auth_token'));
      } catch (error) {
        return Promise.resolve(null);
      }
    }

    function attempt(attemptIndex) {
      return readTokenOnce().then(function(token) {
        if (token) return token;
        if (attemptIndex >= maxAttempts) return null;

        return delay(retryDelayMs).then(function() {
          return attempt(attemptIndex + 1);
        });
      });
    }

    return attempt(1);
  }

  function normalizeProjectsPayload(data, fallbackPageSize) {
    if (Array.isArray(data)) {
      return {
        projects: data,
        totalPages: 1,
        pageSize: data.length || fallbackPageSize
      };
    }

    var projects = Array.isArray(data && data.projects) ? data.projects : [];
    var totalPages = Number(data && data.totalPages);
    var pageSize = Number(data && data.pageSize);

    return {
      projects: projects,
      totalPages: Number.isFinite(totalPages) && totalPages > 0 ? totalPages : 1,
      pageSize: Number.isFinite(pageSize) && pageSize > 0 ? pageSize : fallbackPageSize
    };
  }

  function fetchProjectsPage(apiBaseUrl, token, page, pageSize, customerId) {
    var queryParams = new URLSearchParams();
    queryParams.set('page', String(page));
    queryParams.set('pageSize', String(pageSize));

    var requestUrl = apiBaseUrl + '/projects?' + queryParams.toString();
    var headers = {
      Authorization: 'Bearer ' + token
    };

    if (customerId) {
      headers['x-shopify-customer-id'] = customerId;
    }

    var requestOptions = {
      headers: headers,
      method: 'GET'
    };

    if (window.authState && typeof window.authState.authenticatedFetch === 'function') {
      return window.authState.authenticatedFetch(requestUrl, requestOptions)
        .then(function(response) {
          if (!response.ok) {
            throw new Error('Project fetch failed with status ' + response.status);
          }
          return response.json();
        });
    }

    return fetch(requestUrl, requestOptions)
      .then(function(response) {
        if (!response.ok) {
          throw new Error('Project fetch failed with status ' + response.status);
        }
        return response.json();
      });
  }

  // Cache the projects-API result for the lifetime of this page load.
  // pruneForeignUserOwnedMediaClipItems and pruneDeletedMediaClipProjects both
  // call fetchCurrentUserProjectIdSet, so sharing a single promise avoids two
  // sequential API round-trips on every cart page load.
  var _currentPageProjectIdSetPromise = null;
  var PROJECT_ID_SET_CACHE_PREFIX = '__mimeo_project_ids_cache_v1::';
  var PROJECT_ID_SET_CACHE_TTL_MS = 5 * 60 * 1000;

  function getProjectIdSetCacheKey(apiBaseUrl, customerId) {
    return PROJECT_ID_SET_CACHE_PREFIX + String(apiBaseUrl || '') + '::' + String(customerId || 'anon');
  }

  function readCachedProjectIds(cacheKey) {
    try {
      var raw = sessionStorage.getItem(cacheKey);
      if (!raw) return null;

      var parsed = JSON.parse(raw);
      if (!parsed || !parsed.savedAt || !parsed.projectIds) return null;

      var ageMs = Date.now() - Number(parsed.savedAt);
      if (!Number.isFinite(ageMs) || ageMs < 0 || ageMs > PROJECT_ID_SET_CACHE_TTL_MS) {
        sessionStorage.removeItem(cacheKey);
        return null;
      }

      return parsed.projectIds;
    } catch (error) {
      return null;
    }
  }

  function writeCachedProjectIds(cacheKey, projectIds) {
    try {
      sessionStorage.setItem(cacheKey, JSON.stringify({
        savedAt: Date.now(),
        projectIds: projectIds || {}
      }));
    } catch (error) {}
  }

  function fetchCurrentUserProjectIdSet() {
    if (_currentPageProjectIdSetPromise) return _currentPageProjectIdSetPromise;
    _currentPageProjectIdSetPromise = _doFetchCurrentUserProjectIdSet();
    return _currentPageProjectIdSetPromise;
  }

  function _doFetchCurrentUserProjectIdSet() {
    var apiBaseUrl = window.API_BASE_URL;
    if (!apiBaseUrl) {
      return Promise.resolve(null);
    }

    return getAuthTokenForProjectValidation().then(function(token) {
      if (!token) return null;

      var pageSize = 100;
      var maxPages = 20;
      var customerId = getShopifyCustomerId();
      var cacheKey = getProjectIdSetCacheKey(apiBaseUrl, customerId);
      var cachedProjectIds = readCachedProjectIds(cacheKey);

      if (cachedProjectIds) {
        return {
          apiBaseUrl: apiBaseUrl,
          token: token,
          customerId: customerId,
          projectIds: cachedProjectIds
        };
      }

      var projectIds = {};

      function fetchPage(page) {
        return fetchProjectsPage(apiBaseUrl, token, page, pageSize, customerId)
          .then(function(data) {
            var normalized = normalizeProjectsPayload(data, pageSize);
            (normalized.projects || []).forEach(function(project) {
              var identifierSet = collectProjectIdentifierSet(project);
              Object.keys(identifierSet).forEach(function(projectId) {
                projectIds[projectId] = true;
              });
            });

            var totalPages = normalized.totalPages || 1;
            if (page >= totalPages || page >= maxPages) {
              return projectIds;
            }

            return fetchPage(page + 1);
          });
      }

      return fetchPage(1).then(function() {
        writeCachedProjectIds(cacheKey, projectIds);
        return {
          apiBaseUrl: apiBaseUrl,
          token: token,
          customerId: customerId,
          projectIds: projectIds
        };
      }).catch(function(error) {
        console.warn('[MediaClip] Failed to fetch project list for cart validation:', error);
        return null;
      });
    });
  }

  function verifyProjectIdAccess(apiBaseUrl, token, customerId, projectId) {
    if (!apiBaseUrl || !token || !projectId) return Promise.resolve(true);

    var headers = {
      Authorization: 'Bearer ' + token
    };
    if (customerId) {
      headers['x-shopify-customer-id'] = customerId;
    }

    return fetch(apiBaseUrl + '/projects/' + encodeURIComponent(projectId), {
      method: 'GET',
      headers: headers
    })
      .then(function(response) {
        // Explicitly treat missing/forbidden projects as invalid for cart usage.
        if (response.status === 404 || response.status === 403) {
          return false;
        }

        if (response.ok) {
          return true;
        }

        // Unknown errors should not trigger destructive cart removals.
        return true;
      })
      .catch(function() {
        return true;
      });
  }

  function shouldShowProjectNameAtCheckout(source, options) {
    var normalizedOptions = options || {};
    if (normalizedOptions.forceCheckoutProjectName === true) return true;
    if (isCheckoutPage()) return true;

    var normalizedSource = normalizeSource(source);
    return normalizedSource !== 'web' && normalizedSource !== 'ios';
  }

  function isClearlyAdditionalLineItem(properties) {
    if (!properties || typeof properties !== 'object') return false;

    if (String(properties._isMainPart || properties.isMainPart || '').toLowerCase() === 'false') {
      return true;
    }

    var sku = String(properties._sku || properties.sku || '').toLowerCase();
    if (sku.indexOf('-addspread') !== -1 || sku.indexOf('-addpage') !== -1) return true;

    return false;
  }

  function getCheckoutLineTitle(item, properties) {
    return String((item && item.title) || (properties && (properties._title || properties.title)) || '').trim();
  }

  function isPrintModuleProperties(properties) {
    return String((properties && (properties._moduleType || properties.moduleType)) || '').toLowerCase() === 'print';
  }

  function isYourPrintOrderItem(item, properties) {
    return getCheckoutLineTitle(item, properties).toLowerCase().indexOf('your print order') !== -1;
  }

  function findSiblingPrintProjectName(cartSnapshot, currentItem) {
    var currentProps = (currentItem && currentItem.properties) || {};
    var currentProjectId = String(currentProps._projectId || currentProps.projectId || '').trim();
    var currentEtag = String(currentProps._etag || currentProps.etag || '').trim();

    for (var index = 0; index < ((cartSnapshot && cartSnapshot.items) || []).length; index++) {
      var sibling = cartSnapshot.items[index];
      if (!sibling || sibling.key === currentItem.key || !sibling.properties) continue;

      var siblingProps = sibling.properties;
      if (!isPrintModuleProperties(siblingProps) || isYourPrintOrderItem(sibling, siblingProps)) continue;

      var siblingProjectId = String(siblingProps._projectId || siblingProps.projectId || '').trim();
      var siblingEtag = String(siblingProps._etag || siblingProps.etag || '').trim();

      var sameProjectId = currentProjectId && siblingProjectId && currentProjectId === siblingProjectId;
      var sameEtag = currentEtag && siblingEtag && currentEtag === siblingEtag;
      if (!sameProjectId && !sameEtag) continue;

      var candidate = String(siblingProps._projectName || siblingProps['Project Name'] || siblingProps.projectName || '').trim();
      if (candidate) return candidate;
    }

    return '';
  }

  function resolveCheckoutProjectNameForItem(cartSnapshot, item, options) {
    var normalizedOptions = options || {};
    if (normalizedOptions.forceCheckoutProjectName !== true || !item || !item.properties) return '';

    var properties = item.properties;
    if (isYourPrintOrderItem(item, properties) || isClearlyAdditionalLineItem(properties)) {
      return '';
    }

    var hiddenProjectName = String(properties._projectName || properties.projectName || '').trim();
    if (hiddenProjectName) return hiddenProjectName;

    if (isPrintModuleProperties(properties)) {
      return findSiblingPrintProjectName(cartSnapshot, item);
    }

    return '';
  }

  function buildTransformedProperties(properties, source, options) {
    var transformed = {};
    var changed = false;
    var rawProjectName = '';
    var existingDisplayProjectName = properties ? properties['Project Name'] : undefined;
    var normalizedOptions = options || {};

    Object.keys(properties || {}).forEach(function(key) {
      var value = properties[key];

      if (RAW_PROPS.indexOf(key) !== -1) {
        changed = true;
        // Transform raw property to prefixed version for checkout hiding
        // (prefixed props stay in order data but aren't displayed by Shopify checkout)
        transformed['_' + key] = value;
        if (key === 'projectName' && value !== undefined && value !== null && String(value).trim() !== '') {
          rawProjectName = String(value);
        }
      } else if (key !== 'Project Name') {
        transformed[key] = value;
      }
    });

    if (!rawProjectName && properties && properties._projectName !== undefined && properties._projectName !== null) {
      var hiddenProjectName = String(properties._projectName).trim();
      if (hiddenProjectName) {
        rawProjectName = hiddenProjectName;
      }
    }

    if (!rawProjectName && normalizedOptions.checkoutProjectNameOverride) {
      rawProjectName = String(normalizedOptions.checkoutProjectNameOverride).trim();
    }

    if (shouldShowProjectNameAtCheckout(source, normalizedOptions) && rawProjectName) {
      if (String(existingDisplayProjectName || '') !== rawProjectName) {
        changed = true;
      }
      transformed['Project Name'] = rawProjectName;
    } else if (
      shouldShowProjectNameAtCheckout(source, normalizedOptions) &&
      properties &&
      properties['Project Name'] !== undefined &&
      properties['Project Name'] !== null
    ) {
      transformed['Project Name'] = properties['Project Name'];
    }

    // Ensure no raw keys in output (only prefixed versions + display-friendly aliases)
    RAW_PROPS.forEach(function(rawKey) {
      delete transformed[rawKey];
    });

    if (!shouldShowProjectNameAtCheckout(source, normalizedOptions) || isClearlyAdditionalLineItem(transformed)) {
      delete transformed['Project Name'];
    }

    if (
      existingDisplayProjectName !== undefined &&
      existingDisplayProjectName !== null &&
      transformed['Project Name'] === undefined
    ) {
      changed = true;
    }

    if (transformed._source !== source) {
      transformed._source = source;
      changed = true;
    }

    return { changed: changed, properties: transformed };
  }

  function getMediaClipProjectId(item) {
    if (!item || !item.properties) return '';
    return item.properties._projectId || item.properties.projectId || '';
  }

  function getMediaClipUserId(item) {
    if (!item || !item.properties) return '';
    return getMediaClipProperty(item, ['_userId', 'userId', 'userid']);
  }

  function getMediaClipModuleType(item) {
    if (!item || !item.properties) return '';
    return item.properties._moduleType || item.properties.moduleType || '';
  }

  function getMediaClipEtag(item) {
    if (!item || !item.properties) return '';
    return item.properties._etag || item.properties.etag || '';
  }

  function getMediaClipProperty(item, keys) {
    if (!item || !item.properties || !Array.isArray(keys)) return '';

    for (var index = 0; index < keys.length; index++) {
      var key = keys[index];
      var value = item.properties[key];
      if (value !== undefined && value !== null && String(value).trim() !== '') {
        return String(value).trim();
      }
    }

    return '';
  }

  function getMediaClipCreationTimestamp(item) {
    var creationDate = getMediaClipProperty(item, ['_creationDate', 'creationDate']);
    if (!creationDate) return 0;

    var parsedTimestamp = Date.parse(creationDate);
    return isNaN(parsedTimestamp) ? 0 : parsedTimestamp;
  }

  function getMediaClipVariantIdentity(item) {
    if (!item) return '';

    var variantId = item.variant_id || (item.variant && item.variant.id) || item.id || '';
    var sku = item.sku || '';
    var title = (item.product && item.product.title) || item.title || '';

    return [variantId, sku, title].join('::');
  }

  function getNewestMediaClipItem(items) {
    var newestItem = null;
    var newestTimestamp = -1;

    (items || []).forEach(function(item, index) {
      var itemTimestamp = getMediaClipCreationTimestamp(item);
      if (!newestItem || itemTimestamp > newestTimestamp || (itemTimestamp === newestTimestamp && index > -1)) {
        newestItem = item;
        newestTimestamp = itemTimestamp;
      }
    });

    return newestItem;
  }

  function getMediaClipMainGroupKey(item) {
    var projectIdentity = getMediaClipProjectIdentity(item);
    if (!projectIdentity) return '';

    var moduleType = getMediaClipModuleType(item) || '';
    // Always include variant identity so distinct products from the same
    // project/module are not treated as duplicates.
    return [projectIdentity, moduleType, getMediaClipVariantIdentity(item)].join('::');
  }

  function getMediaClipProjectIdentity(item) {
    var projectId = getMediaClipProjectId(item);
    if (projectId) return 'project:' + projectId;

    var appleProjectId = getMediaClipProperty(item, ['_appleProjectId', 'appleProjectId']);
    if (appleProjectId) return 'apple:' + appleProjectId;

    var shareProjectId = getMediaClipProperty(item, ['_shareProjectId', 'shareProjectId']);
    if (shareProjectId) return 'share:' + shareProjectId;

    var projectName = getMediaClipProperty(item, ['_projectName', 'projectName']);
    var creationDate = getMediaClipProperty(item, ['_creationDate', 'creationDate']);
    var userId = getMediaClipProperty(item, ['_userId', 'userId', 'userid']);

    if (projectName && creationDate) {
      return 'meta:' + projectName + '::' + creationDate + '::' + userId;
    }

    return '';
  }

  function hasMediaClipProjectId(item) {
    return Boolean(getMediaClipProjectId(item));
  }

  function hasMediaClipIdentity(item) {
    return Boolean(getMediaClipProjectIdentity(item));
  }

  function isMediaClipAdditionalItem(item) {
    if (!item || !item.properties) return false;
    if (!hasMediaClipIdentity(item)) return false;

    if (String(item.properties._isMainPart || item.properties.isMainPart || '').toLowerCase() === 'false') {
      return true;
    }

    var sku = (item.sku || '').toLowerCase();

    return sku.indexOf('-addspread') !== -1 || sku.indexOf('-addpage') !== -1;
  }

  function getMediaClipParentKey(item) {
    if (!item) return '';
    return item.parent_key || (item.parent_relationship && item.parent_relationship.parent_key) || '';
  }

  function isMediaClipMainItem(item) {
    if (!item || !item.properties) return false;
    if (!hasMediaClipIdentity(item)) return false;
    if (isMediaClipAdditionalItem(item)) return false;
    if (item.properties._isMainPart === 'false') return false;
    if (getMediaClipParentKey(item)) return false;
    return true;
  }

  function reloadCartPageIfDuplicatesWerePruned() {
    if (!isCartPage()) return;

    var refreshKey = '__mimeo_cart_dedup_reloaded';
    try {
      var now = Date.now();
      var lastReloadAt = Number(sessionStorage.getItem(refreshKey) || 0);

      if (Number.isFinite(lastReloadAt) && now - lastReloadAt < 5000) {
        return;
      }

      sessionStorage.setItem(refreshKey, String(now));
    } catch (e) {}

    // Prefer in-place section refresh over full-document reload to avoid
    // disruptive flashes after quantity updates and dedupe passes.
    var cartSections = ['main-cart-items', 'cart-icon-bubble', 'cart-live-region-text', 'main-cart-footer'];
    var canUseSectionRefresh =
      window.routes &&
      routes.cart_url &&
      typeof fetch === 'function' &&
      typeof DOMParser === 'function';

    if (!canUseSectionRefresh) {
      return;
    }

    var sectionQuery = encodeURIComponent(cartSections.join(','));
    var requestUrl = routes.cart_url + '?sections=' + sectionQuery;
    var applySectionHtml = function(sectionId, sectionHtml) {
      if (!sectionHtml || typeof sectionHtml !== 'string') return;

      var target = document.getElementById(sectionId);
      if (!target) return;

      var parsed = new DOMParser().parseFromString(sectionHtml, 'text/html');
      var source = parsed.getElementById(sectionId);
      target.innerHTML = source ? source.innerHTML : sectionHtml;
    };

    var refreshViaFullCartHtml = function() {
      return fetch(routes.cart_url, {
        method: 'GET',
        credentials: 'same-origin'
      })
        .then(function(response) {
          if (!response.ok) {
            throw new Error('Failed to refresh cart HTML after prune: ' + response.status);
          }
          return response.text();
        })
        .then(function(htmlText) {
          var html = new DOMParser().parseFromString(htmlText, 'text/html');
          cartSections.forEach(function(sectionId) {
            var target = document.getElementById(sectionId);
            var source = html.getElementById(sectionId);
            if (!target || !source) return;
            target.innerHTML = source.innerHTML;
          });
        })
        .catch(function(error) {
          console.error(error);
        });
    };

    fetch(requestUrl, {
      method: 'GET',
      credentials: 'same-origin'
    })
      .then(function(response) {
        if (!response.ok) {
          throw new Error('Failed to refresh cart sections after prune: ' + response.status);
        }
        return response.json();
      })
      .then(function(sections) {
        if (!sections || typeof sections !== 'object') return;

        Object.keys(sections).forEach(function(sectionId) {
          applySectionHtml(sectionId, sections[sectionId]);
        });
      })
      .catch(function() {
        return refreshViaFullCartHtml();
      });
  }

  function announceAutomaticCartRemoval(removedCount) {
    if (!isCartPage()) return;
    if (!removedCount || removedCount < 1) return;

    var localizedTemplate = null;
    if (window.quickOrderListStrings) {
      localizedTemplate = removedCount === 1
        ? window.quickOrderListStrings.itemRemoved
        : window.quickOrderListStrings.itemsRemoved;
    }

    var message = localizedTemplate
      ? String(localizedTemplate).replace('[quantity]', String(removedCount))
      : (removedCount === 1 ? '1 item removed' : String(removedCount) + ' items removed');

    var liveRegion = document.getElementById('cart-live-region-text');
    if (!liveRegion) return;

    liveRegion.textContent = '';
    window.setTimeout(function() {
      liveRegion.textContent = message;
    }, 20);
  }

  var EDIT_FROM_CART_MARKER_KEY = 'mimeo_edit_from_cart_marker';
  var EDIT_FROM_CART_MARKER_MAX_AGE_MS = 30 * 60 * 1000;

  function clearEditFromCartMarker() {
    try {
      sessionStorage.removeItem(EDIT_FROM_CART_MARKER_KEY);
    } catch (_error) {}
  }

  function readActiveEditFromCartMarker() {
    try {
      var raw = sessionStorage.getItem(EDIT_FROM_CART_MARKER_KEY);
      if (!raw) return null;

      var parsed = JSON.parse(raw);
      if (!parsed || parsed.source !== 'cart-edit-project') {
        clearEditFromCartMarker();
        return null;
      }

      if (!parsed.createdAt || typeof parsed.createdAt !== 'number') {
        clearEditFromCartMarker();
        return null;
      }

      if (Date.now() - parsed.createdAt > EDIT_FROM_CART_MARKER_MAX_AGE_MS) {
        clearEditFromCartMarker();
        return null;
      }

      var normalizedProjectId = normalizeProjectId(parsed.projectId);
      if (!normalizedProjectId) {
        clearEditFromCartMarker();
        return null;
      }

      return {
        projectId: normalizedProjectId,
        createdAt: parsed.createdAt
      };
    } catch (_error) {
      clearEditFromCartMarker();
      return null;
    }
  }

  function writeEditFromCartMarker(projectId) {
    var normalizedProjectId = normalizeProjectId(projectId);
    if (!normalizedProjectId) return;

    try {
      sessionStorage.setItem(EDIT_FROM_CART_MARKER_KEY, JSON.stringify({
        source: 'cart-edit-project',
        projectId: normalizedProjectId,
        createdAt: Date.now()
      }));
      // Ensure cart page applies its normalization UI mask immediately after
      // returning from designer edit flow, so superseded rows never flash.
      sessionStorage.setItem('__mimeo_needs_cart_normalize', 'true');
    } catch (_error) {}
  }

  function pruneSupersededEditedProjectItems(cartSnapshot) {
    var marker = readActiveEditFromCartMarker();
    if (!marker) {
      return Promise.resolve(cartSnapshot);
    }

    var items = (cartSnapshot && cartSnapshot.items) || [];
    var matchingItems = [];

    items.forEach(function(item, index) {
      if (!item || !item.properties) return;

      var projectId = normalizeProjectId(getMediaClipProjectId(item));
      if (!projectId || projectId !== marker.projectId) return;

      // Only MAIN lines are candidates. Additional Pages / Additional Spreads
      // lines share the project id but carry their own _etag/_creationDate, so
      // including them here put them in a different session bucket from their
      // own main line and zeroed the shopper's paid add-ons on the way back
      // from Edit Project. Child lines follow their parent; they are never
      // superseded independently.
      if (isMediaClipAdditionalItem(item)) return;

      matchingItems.push({ item: item, index: index });
    });

    if (matchingItems.length < 2) {
      clearEditFromCartMarker();
      return Promise.resolve(cartSnapshot);
    }

    var bucketsBySession = {};

    matchingItems.forEach(function(entry) {
      var item = entry.item;
      var etag = String(getMediaClipEtag(item) || '').trim();
      var creationDate = getMediaClipProperty(item, ['_creationDate', 'creationDate']);
      var sessionKey = etag || creationDate || ('fallback:' + String(item.key || entry.index));

      if (!bucketsBySession[sessionKey]) bucketsBySession[sessionKey] = [];
      bucketsBySession[sessionKey].push(entry);
    });

    var keepSessionKey = '';
    var keepScore = -1;
    var keepIndex = -1;

    Object.keys(bucketsBySession).forEach(function(sessionKey) {
      var entries = bucketsBySession[sessionKey] || [];
      var newestEntry = null;
      var newestTimestamp = -1;

      entries.forEach(function(entry) {
        var timestamp = getMediaClipCreationTimestamp(entry.item);
        if (!newestEntry || timestamp > newestTimestamp || (timestamp === newestTimestamp && entry.index > newestEntry.index)) {
          newestEntry = entry;
          newestTimestamp = timestamp;
        }
      });

      var score = newestTimestamp;
      var tieBreakerIndex = newestEntry ? newestEntry.index : -1;
      if (
        !keepSessionKey ||
        score > keepScore ||
        (score === keepScore && tieBreakerIndex > keepIndex)
      ) {
        keepSessionKey = sessionKey;
        keepScore = score;
        keepIndex = tieBreakerIndex;
      }
    });

    var updates = {};

    Object.keys(bucketsBySession).forEach(function(sessionKey) {
      if (sessionKey === keepSessionKey) return;

      (bucketsBySession[sessionKey] || []).forEach(function(entry) {
        if (!entry || !entry.item || !entry.item.key) return;
        updates[entry.item.key] = 0;
      });
    });

    if (Object.keys(updates).length === 0) {
      clearEditFromCartMarker();
      return Promise.resolve(cartSnapshot);
    }

    return runSerializedCartMutation(function() {
      return fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: updates })
      });
    })
      .then(function(response) {
        if (!response.ok) {
          throw new Error('Failed to prune superseded edited project items: ' + response.status);
        }
        return response.json();
      })
      .then(function(updatedCart) {
        clearEditFromCartMarker();
        reloadCartPageIfDuplicatesWerePruned();
        return updatedCart;
      })
      .catch(function(error) {
        clearEditFromCartMarker();
        console.error(error);
        return cartSnapshot;
      });
  }

  function pruneDuplicateAdditionalMediaClipItems(cartSnapshot) {
    var items = (cartSnapshot && cartSnapshot.items) || [];
    var grouped = {};
    var updates = {};

    items.forEach(function(item) {
      if (!isMediaClipAdditionalItem(item)) return;

      var projectId = getMediaClipProjectId(item);
      var etag = getMediaClipEtag(item);
      var variantId = item.variant_id || item.id || '';
      var sku = item.sku || '';
      var title = (item.product && item.product.title) || item.title || '';

      // If projectId is missing, still group by variant+sku to catch duplicates
      // This ensures items without complete metadata don't create infinite duplicates
      var groupKey;
      if (projectId) {
        groupKey = [projectId, etag, variantId, sku, title].join('::');
      } else {
        groupKey = ['no-project', variantId, sku, title].join('::');
      }

      if (!grouped[groupKey]) grouped[groupKey] = [];
      grouped[groupKey].push(item);
    });

    Object.keys(grouped).forEach(function(groupKey) {
      var groupItems = grouped[groupKey];
      if (!groupItems || groupItems.length < 2) return;

      var itemToKeep = groupItems[groupItems.length - 1];
      groupItems.forEach(function(item) {
        if (!item || item.key === itemToKeep.key) return;
        updates[item.key] = 0;
      });
    });

    if (Object.keys(updates).length === 0) {
      return Promise.resolve(cartSnapshot);
    }

    return runSerializedCartMutation(function() {
      return fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: updates })
      });
    })
      .then(function(response) {
        if (!response.ok) {
          throw new Error('Failed to prune duplicate MediaClip Additional items: ' + response.status);
        }
        return response.json();
      })
      .then(function(updatedCart) {
        reloadCartPageIfDuplicatesWerePruned();
        return updatedCart;
      })
      .catch(function(error) {
        console.error(error);
        return cartSnapshot;
      });
  }

  function pruneStaleMediaClipEditSessions(cartSnapshot) {
    var items = (cartSnapshot && cartSnapshot.items) || [];
    var grouped = {};

    items.forEach(function(item) {
      if (!item || !item.properties) return;

      var isMediaClipItem = item.properties._app === 'mediaclip' ||
        item.properties.projectId ||
        item.properties._projectId ||
        item.properties.moduleType ||
        item.properties._moduleType;
      if (!isMediaClipItem) return;

      var projectId = getMediaClipProjectId(item);
      var moduleType = getMediaClipModuleType(item);
      if (!projectId || !moduleType) return;

      var groupKey = [projectId, moduleType, getMediaClipVariantIdentity(item)].join('::');
      if (!grouped[groupKey]) grouped[groupKey] = [];
      grouped[groupKey].push(item);
    });

    var updates = {};

    Object.keys(grouped).forEach(function(groupKey) {
      var groupItems = grouped[groupKey];
      if (!groupItems || groupItems.length < 2) return;

      // Keep distinct print variants separate. Within a single project/module/
      // variant group, only remove entries when we can identify an older edit
      // session by etag. No-etag items are preserved unless a newer real-etag
      // session exists for the same variant group.
      var EMPTY_ETAG_SENTINEL = '__no_etag__';
      var itemsByEtag = {};
      groupItems.forEach(function(item) {
        var etag = getMediaClipEtag(item) || EMPTY_ETAG_SENTINEL;
        if (!itemsByEtag[etag]) {
          itemsByEtag[etag] = [];
        }
        itemsByEtag[etag].push(item);
      });

      var realEtags = Object.keys(itemsByEtag).filter(function(etag) {
        return etag !== EMPTY_ETAG_SENTINEL;
      });

      if (realEtags.length === 0) {
        var newestNoEtagItem = getNewestMediaClipItem(groupItems);
        groupItems.forEach(function(item) {
          if (!newestNoEtagItem || item.key === newestNoEtagItem.key) return;
          updates[item.key] = 0;
        });
        return;
      }

      var etagToKeep = realEtags[0];
      var newestKeepItem = getNewestMediaClipItem(itemsByEtag[etagToKeep]);
      var etagToKeepTimestamp = newestKeepItem ? getMediaClipCreationTimestamp(newestKeepItem) : 0;
      var etagToKeepLastIndex = newestKeepItem ? groupItems.indexOf(newestKeepItem) : -1;

      realEtags.forEach(function(etag) {
        var itemsWithThisEtag = itemsByEtag[etag] || [];
        var newestItemForEtag = getNewestMediaClipItem(itemsWithThisEtag);
        var newestTimestampForEtag = newestItemForEtag ? getMediaClipCreationTimestamp(newestItemForEtag) : 0;
        var newestIndexForEtag = newestItemForEtag ? groupItems.indexOf(newestItemForEtag) : -1;

        if (
          newestTimestampForEtag > etagToKeepTimestamp ||
          (newestTimestampForEtag === etagToKeepTimestamp && newestIndexForEtag > etagToKeepLastIndex)
        ) {
          etagToKeep = etag;
          etagToKeepTimestamp = newestTimestampForEtag;
          etagToKeepLastIndex = newestIndexForEtag;
        }
      });

      Object.keys(itemsByEtag).forEach(function(etag) {
        var itemsWithThisEtag = itemsByEtag[etag] || [];
        if (etag === etagToKeep) {
          var newestItemForEtag = getNewestMediaClipItem(itemsWithThisEtag);
          itemsWithThisEtag.forEach(function(item) {
            if (!newestItemForEtag || item.key === newestItemForEtag.key) return;
            updates[item.key] = 0;
          });
          return;
        }

        itemsWithThisEtag.forEach(function(item) {
          updates[item.key] = 0;
        });
      });
    });

    if (Object.keys(updates).length === 0) {
      return Promise.resolve(cartSnapshot);
    }

    return runSerializedCartMutation(function() {
      return fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: updates })
      });
    })
      .then(function(response) {
        if (!response.ok) {
          throw new Error('Failed to prune stale MediaClip sessions: ' + response.status);
        }
        return response.json();
      })
      .then(function(updatedCart) {
        reloadCartPageIfDuplicatesWerePruned();
        return updatedCart;
      })
      .catch(function(error) {
        console.error(error);
        return cartSnapshot;
      });
  }

  function pruneDuplicateMainMediaClipProjects(cartSnapshot) {
    var items = (cartSnapshot && cartSnapshot.items) || [];
    var groupedMainItems = {};

    items.forEach(function(item) {
      if (!isMediaClipMainItem(item)) return;

      var groupKey = getMediaClipMainGroupKey(item);
      if (!groupKey) return;

      if (!groupedMainItems[groupKey]) groupedMainItems[groupKey] = [];
      groupedMainItems[groupKey].push(item);
    });

    var updates = {};
    var mainKeysToRemove = {};

    Object.keys(groupedMainItems).forEach(function(groupKey) {
      var groupItems = groupedMainItems[groupKey];
      if (!groupItems || groupItems.length < 2) return;

      var itemToKeep = getNewestMediaClipItem(groupItems) || groupItems[groupItems.length - 1];

      groupItems.forEach(function(item) {
        if (!item || item.key === itemToKeep.key) return;
        updates[item.key] = 0;
        mainKeysToRemove[item.key] = true;
      });
    });

    if (Object.keys(mainKeysToRemove).length > 0) {
      items.forEach(function(item) {
        if (!item) return;
        var parentKey = getMediaClipParentKey(item);
        if (parentKey && mainKeysToRemove[parentKey]) {
          updates[item.key] = 0;
          return;
        }

        if (!isMediaClipAdditionalItem(item)) return;
        var projectIdentity = getMediaClipProjectIdentity(item);
        if (!projectIdentity) return;

        var additionalGroupKey = getMediaClipMainGroupKey(item);

        var stillHasMainForProject = groupedMainItems[additionalGroupKey] && groupedMainItems[additionalGroupKey].length > 0;

        if (!stillHasMainForProject) {
          updates[item.key] = 0;
        }
      });
    }

    if (Object.keys(updates).length === 0) {
      return Promise.resolve(cartSnapshot);
    }

    return runSerializedCartMutation(function() {
      return fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: updates })
      });
    })
      .then(function(response) {
        if (!response.ok) {
          throw new Error('Failed to prune duplicate MediaClip main items: ' + response.status);
        }
        return response.json();
      })
      .then(function(updatedCart) {
        reloadCartPageIfDuplicatesWerePruned();
        return updatedCart;
      })
      .catch(function(error) {
        console.error(error);
        return cartSnapshot;
      });
  }

  function pruneForeignUserOwnedMediaClipItems(cartSnapshot) {
    var items = (cartSnapshot && cartSnapshot.items) || [];
    if (!items.length) return Promise.resolve(cartSnapshot);

    var currentIdentities = getCurrentUserIdentitySet();
    if (Object.keys(currentIdentities).length === 0) {
      return Promise.resolve(cartSnapshot);
    }

    var projectIdsToRemove = {};
    var updates = {};

    items.forEach(function(item) {
      if (!item || !item.properties) return;

      var isMediaClipItem = item.properties._app === 'mediaclip' || hasMediaClipIdentity(item);
      if (!isMediaClipItem) return;

      var lineUserId = normalizeIdentityValue(getMediaClipUserId(item));
      if (!lineUserId || currentIdentities[lineUserId]) return;

      updates[item.key] = 0;

      var projectId = getMediaClipProjectId(item);
      if (projectId) {
        projectIdsToRemove[String(projectId)] = true;
      }
    });

    if (Object.keys(projectIdsToRemove).length > 0) {
      items.forEach(function(item) {
        if (!item || !item.properties) return;

        var itemProjectId = getMediaClipProjectId(item);
        if (!itemProjectId || !projectIdsToRemove[String(itemProjectId)]) return;

        updates[item.key] = 0;
      });
    }

    if (Object.keys(updates).length === 0) {
      return Promise.resolve(cartSnapshot);
    }

    return runSerializedCartMutation(function() {
      return fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: updates })
      });
    })
      .then(function(response) {
        if (!response.ok) {
          throw new Error('Failed to remove foreign-user MediaClip cart items: ' + response.status);
        }
        return response.json();
      })
      .then(function(updatedCart) {
        announceAutomaticCartRemoval(Object.keys(updates).length);
        return updatedCart;
      })
      .catch(function(error) {
        console.error(error);
        return cartSnapshot;
      });
  }

  function pruneDeletedMediaClipProjects(cartSnapshot) {
    var items = (cartSnapshot && cartSnapshot.items) || [];
    if (!items.length) return Promise.resolve(cartSnapshot);

    var mediaclipProjectIds = {};
    items.forEach(function(item) {
      if (!item || !item.properties) return;

      var isMediaClipItem = item.properties._app === 'mediaclip' || hasMediaClipIdentity(item);
      if (!isMediaClipItem) return;

      var isSharedProjectLine = item.properties._shareProjectId || item.properties.shareProjectId;
      if (isSharedProjectLine) return;

      var projectId = getMediaClipProjectId(item);
      if (!projectId) return;

      mediaclipProjectIds[normalizeProjectId(projectId)] = true;
    });

    if (Object.keys(mediaclipProjectIds).length === 0) {
      return Promise.resolve(cartSnapshot);
    }

    return fetchCurrentUserProjectIdSet().then(function(projectValidationContext) {
      // If project list couldn't be resolved, avoid accidental removals.
      if (!projectValidationContext || !projectValidationContext.projectIds) return cartSnapshot;

      var projectIdSet = projectValidationContext.projectIds;

      var deletedProjectIds = {};
      Object.keys(mediaclipProjectIds).forEach(function(projectId) {
        if (!projectIdSet[projectId]) {
          deletedProjectIds[projectId] = true;
        }
      });

      if (Object.keys(deletedProjectIds).length === 0) {
        return cartSnapshot;
      }

      var candidateProjectIds = Object.keys(deletedProjectIds);
      var verificationTasks = candidateProjectIds.map(function(projectId) {
        return verifyProjectIdAccess(
          projectValidationContext.apiBaseUrl,
          projectValidationContext.token,
          projectValidationContext.customerId,
          projectId
        ).then(function(isAccessible) {
          return {
            projectId: projectId,
            isAccessible: isAccessible
          };
        });
      })

      return Promise.all(verificationTasks)
        .then(function(verificationResults) {
          var confirmedInvalidProjectIds = {};
          verificationResults.forEach(function(result) {
            if (!result || result.isAccessible) return;
            confirmedInvalidProjectIds[result.projectId] = true;
          });

          if (Object.keys(confirmedInvalidProjectIds).length === 0) {
            return cartSnapshot;
          }

          var updates = {};
          items.forEach(function(item) {
            if (!item || !item.properties) return;

            var itemProjectId = normalizeProjectId(getMediaClipProjectId(item));
            if (!itemProjectId || !confirmedInvalidProjectIds[itemProjectId]) return;

            updates[item.key] = 0;
          });

          if (Object.keys(updates).length === 0) {
            return cartSnapshot;
          }

          return runSerializedCartMutation(function() {
            return fetch('/cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ updates: updates })
            });
          })
            .then(function(response) {
              if (!response.ok) {
                throw new Error('Failed to remove deleted MediaClip project items: ' + response.status);
              }
              return response.json();
            })
            .then(function(updatedCart) {
              return updatedCart;
            });
        })
        .catch(function(error) {
          console.error(error);
          return cartSnapshot;
        });
    });
  }

  function normalizeCartProperties(cartSnapshot, source, options, recursionState) {
    var itemToUpdate = null;
    var transformedProps = null;
    var normalizedOptions = options || {};

    // `buildTransformedProperties` flags a line as changed whenever
    // `transformed._source !== source`, and this function recurses on the cart
    // it just wrote. If the value we send is not byte-identical to the value
    // Shopify stores (untrimmed input, or a blank that the Cart API drops), that
    // comparison never converges and each pass issues another /cart/change.js.
    // Comparing the NORMALIZED source removes the common trigger; the pass
    // budget below is the backstop for any other non-converging property.
    var normalizedSource = normalizeSource(source);
    var state = recursionState || {
      passes: 0,
      maxPasses: Math.max(10, ((cartSnapshot && cartSnapshot.items ? cartSnapshot.items.length : 0) * 3) + 10)
    };

    for (var index = 0; index < (cartSnapshot.items || []).length; index++) {
      var item = cartSnapshot.items[index];
      if (!item || !item.properties) continue;

      var itemHasRawProperties = hasRawProperties(item.properties);
      var resolvedCheckoutProjectName = resolveCheckoutProjectNameForItem(cartSnapshot, item, normalizedOptions);
      var needsCheckoutProjectNameCleanup =
        normalizedOptions.forceCheckoutProjectName === true &&
        item.properties['Project Name'] !== undefined &&
        item.properties['Project Name'] !== null &&
        String(item.properties['Project Name']).trim() !== '' &&
        resolvedCheckoutProjectName === '';

      var needsCheckoutProjectNameAlias =
        normalizedOptions.forceCheckoutProjectName === true &&
        (item.properties['Project Name'] === undefined || item.properties['Project Name'] === null || String(item.properties['Project Name']).trim() === '') &&
        resolvedCheckoutProjectName !== '';

      if (!itemHasRawProperties && !needsCheckoutProjectNameAlias && !needsCheckoutProjectNameCleanup) continue;

      // Some create/edit flows return raw MediaClip properties without the
      // usual `_app`, `projectId`, or `moduleType` markers. The raw keys are
      // specific enough to use as a normalization signal by themselves.
      var isMediaClipItem = item.properties._app === 'mediaclip' ||
                            item.properties.projectId ||
                            item.properties._projectId ||
                            item.properties.moduleType ||
                            item.properties._moduleType ||
                            itemHasRawProperties;

      if (!isMediaClipItem) continue;

      var transformed = buildTransformedProperties(item.properties, normalizedSource, Object.assign({}, normalizedOptions, {
        checkoutProjectNameOverride: resolvedCheckoutProjectName
      }));
      if (!transformed.changed) continue;

      itemToUpdate = item;
      transformedProps = transformed.properties;
      break;
    }

    if (!itemToUpdate) {
      return Promise.resolve(cartSnapshot);
    }

    if (state.passes >= state.maxPasses) {
      // A property is not converging. Stop rather than issue an unbounded
      // stream of cart writes; the cart is left usable and the checkout guard
      // still records an un-normalized breadcrumb.
      console.warn(
        '[Cart Normalization] Pass budget exhausted after ' + state.passes +
        ' writes; leaving remaining properties un-normalized.'
      );
      return Promise.resolve(cartSnapshot);
    }

    state.passes += 1;

    function postTransformedProperties() {
      return runSerializedCartMutation(function() {
        // `itemToUpdate` comes from a cart snapshot taken BEFORE this write was
        // queued, and the shopper can change the quantity in that window (the
        // cart page's own quantity input queues behind the same mutation lock).
        // Sending the snapshot quantity silently reverted that edit — a line
        // set to 11 went back to 10 on the next normalization pass. Re-read the
        // line here, inside the queue, so the quantity we write is the current
        // one and this call only ever rewrites properties.
        return fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' })
          .then(function(response) {
            return response && response.ok ? response.json() : null;
          })
          .catch(function() {
            return null;
          })
          .then(function(freshCart) {
            var currentQuantity = itemToUpdate.quantity;

            if (freshCart && Array.isArray(freshCart.items)) {
              var current = null;
              for (var index = 0; index < freshCart.items.length; index++) {
                var candidate = freshCart.items[index];
                if (candidate && candidate.key === itemToUpdate.key) {
                  current = candidate;
                  break;
                }
              }

              // The line is gone under this key — its properties were already
              // rewritten (which changes the key), or it was removed. Writing
              // to a key that no longer exists creates nothing useful and can
              // fail the whole pass, so hand the fresh cart back and let the
              // caller re-evaluate it.
              if (!current) {
                return { skipped: true, cart: freshCart };
              }

              currentQuantity = current.quantity;
            }

            return fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                id: itemToUpdate.key,
                quantity: currentQuantity,
                properties: transformedProps
              })
            }).then(function(response) {
              return { skipped: false, response: response };
            });
          });
      })
        .then(function(result) {
          if (result && result.skipped) {
            return result.cart;
          }

          var response = result && result.response;
          if (!response || !response.ok) {
            throw new Error('Failed to transform cart properties: ' + (response ? response.status : 'no response'));
          }
          return response.json();
        });
    }

    return postTransformedProperties()
      .catch(function(error) {
        // MediaClip can hold the cart briefly ("Cart conflicted with another
        // request"), so give a failed write exactly one queued retry.
        console.warn('[Cart Normalization] Retrying property transform after failure:', error);
        return postTransformedProperties();
      })
      .then(function(updatedCart) {
        return normalizeCartProperties(updatedCart, source, options, state);
      })
      .catch(function(error) {
        console.error('[Cart Normalization] Error:', error);
        // Return snapshot to allow cart display to continue
        return cartSnapshot;
      });
  }

  function normalizeCurrentCartOnPageLoad() {
    if (!shouldRun()) {
      return Promise.resolve();
    }

    var skipDestructivePrunePasses = false;
    try {
      var continueShoppingFlow = sessionStorage.getItem('__mimeo_continue_shopping_flow') === 'true';
      var continueShoppingAt = Number(sessionStorage.getItem('__mimeo_continue_shopping_at') || 0);
      if (continueShoppingFlow) {
        var ageMs = continueShoppingAt ? (Date.now() - continueShoppingAt) : 0;
        // Only honor the flag for a short window around the continue-shopping round trip.
        if (!continueShoppingAt || ageMs < 10 * 60 * 1000) {
          skipDestructivePrunePasses = true;
        }
      }
      sessionStorage.removeItem('__mimeo_continue_shopping_flow');
      sessionStorage.removeItem('__mimeo_continue_shopping_at');
    } catch (e) {}

    return fetch('/cart.js')
      .then(function(response) {
        if (!response.ok) {
          throw new Error('Failed to fetch cart: ' + response.status);
        }
        return response.json();
      })
      .then(function(cart) {
        var shouldRunDestructivePruning = window.__mimeoEnableCartPruning === true;

        return pruneSupersededEditedProjectItems(cart)
          .then(function(editPrunedCart) {
            if (skipDestructivePrunePasses || !shouldRunDestructivePruning) {
              return normalizeCartProperties(editPrunedCart, getSource());
            }

            return pruneForeignUserOwnedMediaClipItems(editPrunedCart)
              .then(function(ownerPrunedCart) {
                return pruneDeletedMediaClipProjects(ownerPrunedCart);
              })
              .then(function(projectPrunedCart) {
                return pruneStaleMediaClipEditSessions(projectPrunedCart);
              })
              .then(function(prunedCart) {
                return pruneDuplicateMainMediaClipProjects(prunedCart);
              })
              .then(function(prunedMainCart) {
                return pruneDuplicateAdditionalMediaClipItems(prunedMainCart);
              })
              .then(function(dedupedCart) {
                return normalizeCartProperties(dedupedCart, getSource());
              });
          });
      })
      .catch(function(error) {
        console.error(error);
      });
  }

  function normalizeCartPropertiesForCheckout() {
    if (!shouldRun()) return Promise.resolve();

    // Holds the last cart we managed to read, so a failure downstream still
    // hands the caller a usable snapshot. Resolving `undefined` on failure made
    // recordUnnormalizedCheckoutBreadcrumb() a no-op in exactly the case it
    // exists to record.
    var lastKnownCart = null;

    return fetch('/cart.js')
      .then(function(response) {
        if (!response.ok) throw new Error('Failed to fetch cart: ' + response.status);
        return response.json();
      })
      .then(function(cart) {
        lastKnownCart = cart;
        // Force checkout display aliases while we are still on /cart before submit.
        return normalizeCartProperties(cart, getSource(), { forceCheckoutProjectName: true });
      })
      .catch(function(error) {
        console.error(error);
        return lastKnownCart || undefined;
      });
  }

  function setupEditProjectLinkGuard() {
    // Intercept "Edit Project" link clicks and wait for any in-flight cart normalization
    // to complete before navigating to /tools/customize. Without this guard, our
    // sequential REST mutations (pruning + property rename) can still be processing on
    // Shopify's backend when the designer's first cartLinesUpdate fires, causing a
    // "Cart conflicted with another request" error inside the MediaClip designer.
    //
    // If normalization is already settled, the .then() fires in the next microtask
    // tick — imperceptible to the user. If it is still in-flight, navigation is
    // deferred until the pipeline finishes.
    document.addEventListener('click', function(event) {
      var link = event.target.closest
        ? event.target.closest('.cart-item__edit-project a[href*="tools/customize"]')
        : null;

      if (!link) return;

      event.preventDefault();

      if (link.dataset.mcEditNavInProgress === 'true') {
        return;
      }

      link.dataset.mcEditNavInProgress = 'true';

      var normalizationPromise = window.__mimeoCartPropertyNormalizationPromise;
      var waitForNormalization = normalizationPromise && typeof normalizationPromise.then === 'function'
        ? normalizationPromise.catch(function() { /* ignore normalization errors — navigate anyway */ })
        : Promise.resolve();

      var waitForQueuedMutations = typeof window.__mimeoWaitForCartMutations === 'function'
        ? window.__mimeoWaitForCartMutations().catch(function() { /* ignore queue wait errors */ })
        : Promise.resolve();

      var waitForQuietPeriod = function() {
        var finishedAt = Number(window.__mimeoLastCartMutationFinishedAt || 0);
        if (!finishedAt) return Promise.resolve();

        var QUIET_PERIOD_MS = 700;
        var elapsed = Date.now() - finishedAt;
        var remaining = QUIET_PERIOD_MS - elapsed;
        if (remaining <= 0) return Promise.resolve();

        return new Promise(function(resolve) {
          window.setTimeout(resolve, remaining);
        });
      };

      var href = link.href;
      var didNavigate = false;

      try {
        var parsedHref = new URL(href, window.location.origin);
        var editProjectId = parsedHref.searchParams.get('projectId');
        writeEditFromCartMarker(editProjectId);
      } catch (_error) {}

      function navigateToEditProject() {
        if (didNavigate) return;
        didNavigate = true;
        window.location.href = href;
      }

      // Fail-open safety: never block Edit Project navigation indefinitely if
      // any async pre-check stalls.
      var NAVIGATION_TIMEOUT_MS = 2500;
      var fallbackTimer = window.setTimeout(function() {
        navigateToEditProject();
      }, NAVIGATION_TIMEOUT_MS);

      Promise.resolve()
        .then(function() {
          return waitForNormalization;
        })
        .then(function() {
          return waitForQueuedMutations;
        })
        .then(function() {
          return waitForQuietPeriod();
        })
        .then(function() {
          // Do not run cart validation/fix during Edit Project navigation.
          // Validation can mutate cart lines and remove items while the user
          // only intended to open the editor.
          navigateToEditProject();
        })
        .finally(function() {
          window.clearTimeout(fallbackTimer);
          delete link.dataset.mcEditNavInProgress;
        });
    }, true);
  }

  // Diagnostic only: never blocks checkout. Records why raw keys reached the
  // hosted checkout, which cannot load theme assets to report it itself.
  function recordUnnormalizedCheckoutBreadcrumb(cartSnapshot) {
    try {
      var items = (cartSnapshot && Array.isArray(cartSnapshot.items)) ? cartSnapshot.items : null;
      if (!items) return;

      var leaking = items
        .filter(function(item) { return item && hasRawProperties(item.properties); })
        .map(function(item) {
          return {
            key: item.key,
            title: item.product_title || item.title || '',
            rawKeys: RAW_PROPS.filter(function(rawKey) {
              return Object.prototype.hasOwnProperty.call(item.properties, rawKey);
            })
          };
        });

      if (!leaking.length) {
        sessionStorage.removeItem('__mimeo_raw_props_at_checkout');
        return;
      }

      console.warn('[Cart Normalization] Submitting to checkout with raw properties still present:', leaking);
      sessionStorage.setItem('__mimeo_raw_props_at_checkout', JSON.stringify({
        at: new Date().toISOString(),
        path: window.location.pathname,
        items: leaking
      }));
    } catch (error) {
      console.warn('[Cart Normalization] Breadcrumb failed:', error);
    }
  }

  function setupNativeCheckoutSubmitGuard() {
    var form = document.getElementById('cart');
    if (!form || form.dataset.mcCheckoutSubmitGuardBound === 'true') return;

    form.dataset.mcCheckoutSubmitGuardBound = 'true';

    form.addEventListener('submit', function(event) {
      if (!shouldRun()) return;

      logCheckoutPayloadDebug('native submit handler', form);

      if (form.dataset.mcCheckoutSubmitting === 'true') {
        delete form.dataset.mcCheckoutSubmitting;
        return;
      }

      var submitter = event.submitter || null;
      var isCheckoutSubmit = submitter
        ? submitter.name === 'checkout' || submitter.id === 'checkout'
        : document.activeElement && document.activeElement.id === 'checkout';

      if (!isCheckoutSubmit) return;
      if (form.dataset.mcCheckoutNormalizing === 'true') {
        event.preventDefault();
        return;
      }

      event.preventDefault();
      form.dataset.mcCheckoutNormalizing = 'true';

      var waitForQueuedMutations = typeof window.__mimeoWaitForCartMutations === 'function'
        ? window.__mimeoWaitForCartMutations().catch(function(error) {
            console.error('[Cart Normalization] Waiting for queued cart mutations failed:', error);
          })
        : Promise.resolve();

      withFailOpenTimeout(
        Promise.resolve(waitForQueuedMutations),
        CHECKOUT_NORMALIZATION_TIMEOUT_MS,
        'Queued cart mutations before checkout'
      )
        .then(function() {
          var normalizeCartPropertiesNow = window.__mimeoNormalizeCartProperties;
          var normalizationPromise = typeof normalizeCartPropertiesNow === 'function'
            ? normalizeCartPropertiesNow()
            : window.__mimeoCartPropertyNormalizationPromise;

          if (!normalizationPromise || typeof normalizationPromise.then !== 'function') {
            return Promise.resolve();
          }

          // Without this bound, a hung request leaves mcCheckoutNormalizing set
          // and every later checkout click is preventDefault()ed for good.
          return withFailOpenTimeout(
            normalizationPromise,
            CHECKOUT_NORMALIZATION_TIMEOUT_MS,
            'Checkout normalization'
          );
        })
        .catch(function(error) {
          console.error('[Cart Normalization] Checkout normalization failed:', error);
        })
        .then(function(normalizedCart) {
          recordUnnormalizedCheckoutBreadcrumb(normalizedCart);

          form.dataset.mcCheckoutSubmitting = 'true';

          if (submitter && typeof form.requestSubmit === 'function') {
            form.requestSubmit(submitter);
            return;
          }

          form.submit();
        })
        .finally(function() {
          delete form.dataset.mcCheckoutNormalizing;
        });
    }, true);
  }

  function setupCheckoutDebugClickLogger() {
    document.addEventListener('click', function(event) {
      var target = event.target && event.target.closest
        ? event.target.closest('#checkout, button[name="checkout"], input[name="checkout"]')
        : null;

      if (!target) return;

      var form = document.getElementById('cart') || target.closest('form');
      logCheckoutPayloadDebug('capture click logger', form);
    }, true);
  }

  function setCartNormalizingUi(active) {
    var cartItemsRoot = document.querySelector('cart-items');
    if (cartItemsRoot) {
      if (active) {
        cartItemsRoot.setAttribute('data-mc-normalizing', 'true');
        cartItemsRoot.setAttribute('aria-busy', 'true');
      } else {
        cartItemsRoot.removeAttribute('data-mc-normalizing');
        cartItemsRoot.setAttribute('aria-busy', 'false');
      }
    }
    if (!active) {
      // Clear the SSR-less pending flag so the CSS visibility rule lifts
      document.documentElement.removeAttribute('data-mc-cart-normalizing-pending');
      try { sessionStorage.removeItem('__mimeo_needs_cart_normalize'); } catch (e) {}
    }
  }

  function attachCartNormalizationUiMask(normalizationPromise) {
    var needsNormalize = false;
    try { needsNormalize = sessionStorage.getItem('__mimeo_needs_cart_normalize') === 'true'; } catch (e) {}

    // Only mask when we know duplicates may be present (i.e., came from Add to Cart).
    // On plain cart visits the cart renders immediately with no blank-screen delay.
    if (!needsNormalize) {
      setCartNormalizingUi(false);
      return;
    }

    setCartNormalizingUi(true);

    if (!normalizationPromise || typeof normalizationPromise.then !== 'function') {
      setCartNormalizingUi(false);
      return;
    }

    // Safety net: never keep the cart hidden for longer than 4 seconds.
    // If the normalization pipeline is slow (e.g. projects API unavailable),
    // the cart becomes visible after this deadline regardless.
    var MASK_TIMEOUT_MS = 4000;
    var timeoutPromise = new Promise(function(resolve) {
      window.setTimeout(resolve, MASK_TIMEOUT_MS);
    });

    Promise.race([
      Promise.resolve(normalizationPromise).catch(function() {}),
      timeoutPromise
    ]).then(function() {
      setCartNormalizingUi(false);
    });
  }

  // Module-scoped so a second call (bfcache `pageshow`) cancels the previous
  // chain instead of stacking another one. Each chain does 10 x (/cart.js +
  // possible /cart/change.js) at 500ms, so back/forward navigation used to
  // multiply the request load and leak a beforeunload listener per chain.
  var continuousNormalizationTimer = null;
  var continuousNormalizationRun = 0;
  var continuousNormalizationUnloadBound = false;

  function stopCheckoutContinuousNormalization() {
    if (continuousNormalizationTimer) {
      window.clearTimeout(continuousNormalizationTimer);
      continuousNormalizationTimer = null;
    }
    // Invalidate any in-flight chain: its `.then` compares this token.
    continuousNormalizationRun += 1;
  }

  function setupCheckoutContinuousNormalization() {
    // On checkout, items might be added asynchronously after page load.
    // Keep normalizing periodically to catch any late-arriving items with raw properties.
    stopCheckoutContinuousNormalization();

    var runToken = continuousNormalizationRun;
    var normalizeAttempts = 0;
    var maxAttempts = 10; // ~5 seconds at 500ms intervals

    function attemptNormalization() {
      if (runToken !== continuousNormalizationRun) return;

      normalizeAttempts += 1;
      normalizeCartPropertiesForCheckout()
        .catch(function() {})
        .then(function() {
          if (runToken !== continuousNormalizationRun) return;
          if (normalizeAttempts < maxAttempts) {
            // Schedule next normalization attempt
            continuousNormalizationTimer = window.setTimeout(attemptNormalization, 500);
          }
        });
    }

    // Start continuous normalization
    attemptNormalization();

    // Cleanup: stop polling if user leaves the page. Bound once for the life of
    // the document rather than once per chain.
    if (!continuousNormalizationUnloadBound) {
      continuousNormalizationUnloadBound = true;
      window.addEventListener('beforeunload', stopCheckoutContinuousNormalization);
    }
  }

  function init() {
    if (!isCartOrCheckoutPage()) return;

    // Expose lightweight property-only normalization for checkout use.
    // Do NOT expose normalizeCurrentCartOnPageLoad here — calling the full
    // prune/dedup pipeline at checkout time can reload the page mid-submit
    // or leave the cart in a partially-pruned state that MediaClip rejects.
    window.__mimeoNormalizeCartProperties = normalizeCartPropertiesForCheckout;

    if (isCheckoutPage()) {
      // On checkout, only run lightweight property transformation
      // Do NOT run the full pipeline (pruning/deduping)
      // Also set up continuous normalization to catch late-arriving items
      window.__mimeoCartPropertyNormalizationPromise = normalizeCartPropertiesForCheckout();
      setupCheckoutContinuousNormalization();
    } else {
      // On cart page, run the full normalization pipeline
      window.__mimeoCartPropertyNormalizationPromise = normalizeCurrentCartOnPageLoad();
      attachCartNormalizationUiMask(window.__mimeoCartPropertyNormalizationPromise);
    }

    setupEditProjectLinkGuard();
    setupNativeCheckoutSubmitGuard();
    setupCheckoutDebugClickLogger();
  }

  window.addEventListener('pageshow', function(event) {
    if (!isCartOrCheckoutPage()) return;
    if (!event.persisted) return;

    // On bfcache restore, run appropriate normalization based on page type
    window.__mimeoNormalizeCartProperties = normalizeCartPropertiesForCheckout;

    if (isCheckoutPage()) {
      window.__mimeoCartPropertyNormalizationPromise = normalizeCartPropertiesForCheckout();
      setupCheckoutContinuousNormalization();
    } else {
      window.__mimeoCartPropertyNormalizationPromise = normalizeCurrentCartOnPageLoad();
      attachCartNormalizationUiMask(window.__mimeoCartPropertyNormalizationPromise);
    }

    setupNativeCheckoutSubmitGuard();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

(function () {
  'use strict';

  var CART_SECTIONS = ['main-cart-items', 'cart-icon-bubble', 'cart-live-region-text', 'main-cart-footer'];
  var UPDATE_DEBOUNCE_MS = 100;

  var cartApi = null;
  var checkoutButton = null;
  var cartForm = null;
  var cartApiMutationQueue = Promise.resolve();
  var cartApiMutationsInFlight = 0;
  window.__mimeoMediaclipCartActive = false;

  var CART_INIT_NORMALIZATION_TIMEOUT_MS = 8000;

  // Local twin of IIFE 1's helper (separate scope). Fail-open: resolves once the
  // promise settles or the timeout elapses, never rejects.
  function withFailOpenTimeout(promise, timeoutMs, label) {
    if (!promise || typeof promise.then !== 'function') return Promise.resolve();

    return new Promise(function (resolve) {
      var settled = false;
      var timer = window.setTimeout(function () {
        if (settled) return;
        settled = true;
        console.warn('[MediaClip] ' + label + ' timed out after ' + timeoutMs + 'ms; continuing.');
        resolve();
      }, timeoutMs);

      promise
        .then(function () {
          if (settled) return;
          settled = true;
          window.clearTimeout(timer);
          resolve();
        })
        .catch(function (error) {
          if (settled) return;
          settled = true;
          window.clearTimeout(timer);
          console.error('[MediaClip] ' + label + ' failed:', error);
          resolve();
        });
    });
  }

  function getSource() {
    if (typeof window.getSource === 'function') return window.getSource();
    return 'web';
  }

  function normalizeSource(source) {
    var raw = (source === undefined || source === null) ? '' : String(source);
    var normalized = raw.trim().toLowerCase();

    if (!normalized) {
      return (window.Capacitor || window.cordova) ? 'ios' : 'web';
    }

    if (normalized === 'web') return 'web';
    if (normalized === 'ios') return 'ios';

    if (
      normalized.indexOf('ios') !== -1 ||
      normalized.indexOf('capacitor') !== -1 ||
      normalized.indexOf('cordova') !== -1
    ) {
      return 'ios';
    }

    if (
      normalized.indexOf('web') !== -1 ||
      normalized.indexOf('browser') !== -1
    ) {
      return 'web';
    }

    return normalized;
  }

  function shouldUseMediaclipCartFlow() {
    var source = normalizeSource(getSource());
    return source === 'web' || source === 'ios';
  }

  function isNativeQuantityPipelineEnabled() {
    return Boolean(document.querySelector('quantity-input[data-mc-quantity-managed="false"]'));
  }

  function isCartPage() {
    var path = window.location.pathname || '';
    return /\/cart(\/|$)/.test(path);
  }

  function debounce(wait, callback) {
    var timeoutId;
    return function () {
      var args = Array.prototype.slice.call(arguments);
      window.clearTimeout(timeoutId);
      timeoutId = window.setTimeout(function () {
        callback.apply(null, args);
      }, wait);
    };
  }

  function setCheckoutEnabled(enabled) {
    if (!checkoutButton) return;

    var hasVisibleItems = Boolean(
      document.querySelector('#main-cart-items .cart-item:not(.cart-item__additional-hidden)')
    );

    checkoutButton.disabled = !enabled || !hasVisibleItems;
  }

  function setCartErrorMessage(message) {
    var errors = document.getElementById('cart-errors');
    if (errors) {
      errors.textContent = message || '';
    }
  }

  function restoreNativeCartModeUi() {
    cartForm = document.getElementById('cart');
    checkoutButton = document.getElementById('checkout');

    if (cartForm && window.routes && routes.cart_url) {
      cartForm.setAttribute('action', routes.cart_url);
    }

    document.querySelectorAll('quantity-input[data-mc-quantity-managed="true"] .quantity__input').forEach(function (input) {
      input.readOnly = false;
      delete input.dataset.mcQuantityBound;
    });

    document.querySelectorAll('quantity-input[data-mc-quantity-managed="false"]').forEach(function (quantityRoot) {
      quantityRoot.dataset.mcQuantityManaged = 'true';
    });

    document.querySelectorAll('cart-remove-button a[data-mc-remove-bound="true"]').forEach(function (removeLink) {
      delete removeLink.dataset.mcRemoveBound;
    });

    if (checkoutButton) {
      delete checkoutButton.dataset.mcCheckoutBound;
      var hasVisibleItems = Boolean(
        document.querySelector('#main-cart-items .cart-item:not(.cart-item__additional-hidden)')
      );
      checkoutButton.disabled = !hasVisibleItems;
    }

    window.__mimeoUseNativeCartQuantity = false;
    delete window.__mimeoValidateMediaclipCart;
  }

  function applyMediaclipCartModeUi() {
    cartForm = document.getElementById('cart');
    checkoutButton = document.getElementById('checkout');

    if (cartForm && window.routes && routes.cart_url) {
      // Submit through native cart action so checkout always reflects persisted
      // Shopify cart quantities and avoids custom rebuild-side resets.
      cartForm.setAttribute('action', routes.cart_url);
    }

    document.querySelectorAll('quantity-input[data-mc-quantity-managed="true"] .quantity__input').forEach(function (input) {
      input.readOnly = false;
    });

    // Use the native cart.js quantity pipeline for reliable section refresh and
    // persisted line updates. The Mediaclip checkout/validation flow remains active.
    document.querySelectorAll('quantity-input[data-mc-quantity-managed="true"]').forEach(function (quantityRoot) {
      quantityRoot.dataset.mcQuantityManaged = 'false';
    });

    window.__mimeoUseNativeCartQuantity = true;
  }

  function applyUpdatedSections(updatedSections) {
    if (!updatedSections || typeof updatedSections !== 'object') return;

    Object.entries(updatedSections).forEach(function (entry) {
      var sectionId = entry[0];
      var sectionHtml = entry[1];
      if (!sectionHtml) return;

      var container = document.getElementById(sectionId);
      if (!container) return;

      var parsedHtml = new DOMParser().parseFromString(sectionHtml, 'text/html');
      var sourceElement = parsedHtml.getElementById(sectionId);
      container.innerHTML = sourceElement ? sourceElement.innerHTML : sectionHtml;
    });
  }

  function getVisibleCartItems() {
    if (!cartApi || typeof cartApi.getMainAndNonMediaclipItems !== 'function') {
      return [];
    }

    try {
      return cartApi.getMainAndNonMediaclipItems() || [];
    } catch (error) {
      console.error('[MediaClip] Failed to get visible cart items:', error);
      return [];
    }
  }

  function refreshCartSectionsFromServer() {
    if (!window.routes || !routes.cart_url) return Promise.resolve();

    var sectionQuery = encodeURIComponent(CART_SECTIONS.join(','));
    var requestUrl = routes.cart_url + '?sections=' + sectionQuery;

    return fetch(requestUrl, {
      method: 'GET',
      credentials: 'same-origin'
    })
      .then(function(response) {
        if (!response.ok) {
          throw new Error('Failed to refresh cart sections: ' + response.status);
        }
        return response.json();
      })
      .then(function(sections) {
        applyUpdatedSections(sections);
      })
      .catch(function(error) {
        console.error('[MediaClip] Failed to refresh cart sections:', error);
      });
  }

  function hasRenderableSectionsPayload(payload) {
    if (!payload || typeof payload !== 'object') return false;

    return CART_SECTIONS.some(function(sectionId) {
      return typeof payload[sectionId] === 'string' && payload[sectionId].length > 0;
    });
  }

  function getVisibleCartItemByLine(line) {
    if (!line) return null;

    var items = getVisibleCartItems();
    return items.find(function(item) {
      return item && item.identifier && item.identifier.line === line;
    }) || null;
  }

  function saveCartAndRefreshHandlers() {
    return cartApi.saveCart().then(function (saveResult) {
      var sectionPayload = (saveResult && saveResult.updatedSections) || saveResult;

      if (hasRenderableSectionsPayload(sectionPayload)) {
        applyUpdatedSections(sectionPayload);
        return;
      }

      return refreshCartSectionsFromServer();
    }).then(function () {
      rebindUiHandlers();
    });
  }

  function enqueueCartApiMutation(mutationTask) {
    if (typeof mutationTask !== 'function') {
      return Promise.resolve();
    }

    cartApiMutationsInFlight += 1;
    setCheckoutEnabled(false);

    var runTask = function () {
      return Promise.resolve().then(mutationTask);
    };

    var queuedTask = cartApiMutationQueue.then(runTask, runTask);

    cartApiMutationQueue = queuedTask.catch(function () {
      return Promise.resolve();
    });

    return queuedTask.finally(function () {
      cartApiMutationsInFlight = Math.max(0, cartApiMutationsInFlight - 1);
      if (cartApiMutationsInFlight === 0) {
        setCheckoutEnabled(true);
      }
    });
  }

  function waitForCartApiMutationQueue() {
    return cartApiMutationQueue.catch(function () {
      return Promise.resolve();
    });
  }

  function waitForQueuedCartMutations() {
    if (typeof window.__mimeoWaitForCartMutations === 'function') {
      return window.__mimeoWaitForCartMutations().catch(function () {
        return Promise.resolve();
      });
    }

    return Promise.resolve();
  }

  function setMainItemQuantity(cartItem, quantity) {
    if (!cartApi || typeof cartApi.setMainItemQuantity !== 'function') return Promise.resolve();

    return enqueueCartApiMutation(function () {
      setCartErrorMessage('');

      try {
        cartApi.setMainItemQuantity(cartItem, quantity);
        return saveCartAndRefreshHandlers()
          .catch(function (error) {
            console.error('[MediaClip] Quantity update failed:', error);
            setCartErrorMessage(window.cartStrings && window.cartStrings.error ? window.cartStrings.error : 'There was an error while updating your cart. Please try again.');
          });
      } catch (error) {
        console.error('[MediaClip] Quantity update failed:', error);
        setCartErrorMessage(window.cartStrings && window.cartStrings.error ? window.cartStrings.error : 'There was an error while updating your cart. Please try again.');
        return Promise.resolve();
      }
    });
  }

  function setMainItemQuantityByLineFallback(line, quantity) {
    var normalizedLine = Number.parseInt(line, 10);
    if (!Number.isFinite(normalizedLine) || normalizedLine < 1) {
      return Promise.resolve();
    }

    return enqueueCartApiMutation(function () {
      setCartErrorMessage('');

      var cartChangeAjaxUrl = routes && routes.cart_change_url
        ? (routes.cart_change_url.endsWith('.js') ? routes.cart_change_url : routes.cart_change_url + '.js')
        : '/cart/change.js';

      return fetch(cartChangeAjaxUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          line: normalizedLine,
          quantity: quantity,
          sections: CART_SECTIONS,
          sections_url: window.location.pathname
        })
      })
        .then(function(response) {
          if (!response.ok) {
            throw new Error('Fallback quantity update failed: ' + response.status);
          }
          return response.json();
        })
        .then(function(parsedState) {
          if (parsedState && parsedState.sections && hasRenderableSectionsPayload(parsedState.sections)) {
            applyUpdatedSections(parsedState.sections);
            return;
          }

          return refreshCartSectionsFromServer();
        })
        .then(function() {
          rebindUiHandlers();
        })
        .catch(function(error) {
          console.error('[MediaClip] Fallback quantity update failed:', error);
          setCartErrorMessage(window.cartStrings && window.cartStrings.error ? window.cartStrings.error : 'There was an error while updating your cart. Please try again.');
        });
    });
  }

  function getCartRowForLine(line) {
    if (!line) return null;
    return document.querySelector("[data-mc-shopify-cart-item-line='" + line + "']");
  }

  function getCartItemProjectId(cartItem, line) {
    var properties = cartItem && cartItem.properties ? cartItem.properties : {};
    var projectId = properties._projectId || properties.projectId || '';
    if (projectId) return projectId;

    var row = getCartRowForLine(line);
    if (!row || !row.dataset) return '';
    return row.dataset.projectId || row.dataset.mcProjectId || '';
  }

  function getCartItemModuleType(cartItem, line) {
    var properties = cartItem && cartItem.properties ? cartItem.properties : {};
    var moduleType = properties._moduleType || properties.moduleType || '';
    if (moduleType) return moduleType;

    var row = getCartRowForLine(line);
    return row && row.dataset ? row.dataset.mcModule || '' : '';
  }

  function getPrintProjectMainItems(cartItems, projectId) {
    if (!projectId) return [];

    return cartItems.filter(function (item) {
      var itemLine = item && item.identifier && item.identifier.line;
      var itemProjectId = getCartItemProjectId(item, itemLine);
      var itemModuleType = getCartItemModuleType(item, itemLine);
      return itemProjectId === projectId && String(itemModuleType || '').toLowerCase() === 'print';
    });
  }

  function removePrintProjectItems(cartItemsToRemove) {
    if (!cartItemsToRemove || cartItemsToRemove.length === 0) {
      return Promise.resolve();
    }

    return enqueueCartApiMutation(function () {
      setCartErrorMessage('');

      try {
        cartItemsToRemove.forEach(function (item) {
          cartApi.setMainItemQuantity(item, 0);
        });

        return saveCartAndRefreshHandlers().catch(function (error) {
          console.error('[MediaClip] Project removal failed:', error);
          setCartErrorMessage(window.cartStrings && window.cartStrings.error ? window.cartStrings.error : 'There was an error while updating your cart. Please try again.');
        });
      } catch (error) {
        console.error('[MediaClip] Project removal failed:', error);
        setCartErrorMessage(window.cartStrings && window.cartStrings.error ? window.cartStrings.error : 'There was an error while updating your cart. Please try again.');
        return Promise.resolve();
      }
    });
  }

  function setupQuantityChangeButtons() {
    // Intentionally no-op: quantity changes are handled by assets/cart.js.
  }

  function setupRemoveButtons() {
    var visibleCartItems = getVisibleCartItems();

    visibleCartItems.forEach(function (cartItem) {
      var line = cartItem && cartItem.identifier && cartItem.identifier.line;
      if (!line) return;

      var removeControl = document.querySelector("[data-mc-shopify-cart-item-line='" + line + "'] cart-remove-button a, [data-mc-shopify-cart-item-line='" + line + "'] cart-remove-button button");
      if (!removeControl || removeControl.dataset.mcRemoveBound === 'true') return;

      removeControl.dataset.mcRemoveBound = 'true';
      removeControl.addEventListener('click', function (event) {
        event.preventDefault();

        var latestVisibleCartItems = getVisibleCartItems();

        var projectId = getCartItemProjectId(cartItem, line);
        if (!projectId) {
          setMainItemQuantity(cartItem, 0);
          return;
        }

        var projectPrintItems = getPrintProjectMainItems(latestVisibleCartItems, projectId);
        var hasMultiplePrintVariants = projectPrintItems.length > 1;

        if (!hasMultiplePrintVariants) {
          setMainItemQuantity(cartItem, 0);
          return;
        }

        var projectModuleTypes = projectPrintItems
          .map(function (item) {
            var itemLine = item && item.identifier && item.identifier.line;
            return String(getCartItemModuleType(item, itemLine) || '').toLowerCase();
          })
          .filter(function (moduleType) { return Boolean(moduleType); });

        var isPrintProject = projectModuleTypes.length === 0 || projectModuleTypes.some(function (moduleType) {
          return moduleType === 'print';
        });

        if (!isPrintProject) {
          setMainItemQuantity(cartItem, 0);
          return;
        }

        var confirmRemovalPromise;
        if (typeof window.mimeoConfirmPrintProjectRemoval === 'function') {
          confirmRemovalPromise = Promise.resolve(window.mimeoConfirmPrintProjectRemoval());
        } else {
          confirmRemovalPromise = Promise.resolve(window.confirm(
            'Removing this variant will remove the entire print project from your cart. Do you want to continue?'
          ));
        }

        confirmRemovalPromise
          .then(function (shouldContinue) {
            if (!shouldContinue) return;
            removePrintProjectItems(projectPrintItems);
          })
          .catch(function () {
            // Keep default behavior if custom confirmation fails unexpectedly.
          });
      });
    });
  }

  function refreshProjectThumbnails() {
    if (!cartApi || typeof cartApi.refreshMediaclipProjectThumbnails !== 'function') return;

    cartApi.refreshMediaclipProjectThumbnails()
      .then(function (refreshResult) {
        var thumbnails = refreshResult && refreshResult.thumbnailUrlsPerProjectId;
        if (!thumbnails) return;

        Object.entries(thumbnails).forEach(function (entry) {
          var projectId = entry[0];
          var thumbnailUrl = entry[1];
          if (!thumbnailUrl) return;

          var projectElement = document.querySelector("[data-mc-project-id='" + projectId + "']");
          var imageElement = projectElement && projectElement.querySelector('.cart-item__image');

          if (imageElement) {
            imageElement.src = thumbnailUrl;
          }
        });
      })
      .catch(function (error) {
        console.error('[MediaClip] Failed to refresh project thumbnails:', error);
      });
  }

  function validateMediaclipCartForProjectNavigation() {
    if (!cartApi || typeof cartApi.executeMediaclipValidationAndFixCart !== 'function') {
      return Promise.resolve({ allowNavigation: true, shouldReload: false });
    }

    return cartApi.executeMediaclipValidationAndFixCart({ skipRerender: false })
      .then(function(validationResult) {
        if (validationResult && validationResult.result === 'Fixed') {
          return saveCartAndRefreshHandlers()
            .catch(function(error) {
              console.error('[MediaClip] Failed to refresh cart after validation fix:', error);
            })
            .then(function() {
              return { allowNavigation: false, shouldReload: true };
            });
        }

        if (validationResult && validationResult.result === 'Error') {
          setCartErrorMessage('We could not validate this project. Please review your cart and try again.');
          return { allowNavigation: false, shouldReload: false };
        }

        return { allowNavigation: true, shouldReload: false };
      })
      .catch(function(error) {
        console.error('[MediaClip] Project navigation validation failed:', error);
        // Preserve existing behavior in case validation service is temporarily unavailable.
        return { allowNavigation: true, shouldReload: false };
      });
  }

  function runInitialMediaclipCartValidation() {
    return validateMediaclipCartForProjectNavigation()
      .then(function(validationState) {
        if (validationState && validationState.allowNavigation === false) {
          rebindUiHandlers();
        }
      })
      .catch(function(error) {
        console.error('[MediaClip] Initial cart validation failed:', error);
      });
  }

  function setupCheckoutValidation() {
    if (!checkoutButton || checkoutButton.dataset.mcCheckoutBound === 'true') return;

    // When native quantity handling is enabled, avoid pre-checkout cartApi.saveCart()
    // and validation rewrites that can replay stale in-memory quantities.
    if (isNativeQuantityPipelineEnabled()) {
      return;
    }

    checkoutButton.dataset.mcCheckoutBound = 'true';

    checkoutButton.addEventListener('click', function (event) {
      if (!cartApi || typeof cartApi.executeMediaclipValidationAndFixCart !== 'function') {
        return;
      }

      // NOTE: logCheckoutPayloadDebug() is defined in the first IIFE of this
      // file and is NOT in scope here. Calling it threw a ReferenceError on the
      // first statement of this listener — before event.preventDefault() — so
      // the MediaClip validation path never ran and the form fell through to a
      // native submit. Left out deliberately; do not re-add without hoisting
      // the helper to a shared scope.
      event.preventDefault();
      setCheckoutEnabled(false);
      setCartErrorMessage('');

      var normalizeCartPropertiesNow = window.__mimeoNormalizeCartProperties;
      var normalizationPromise = typeof normalizeCartPropertiesNow === 'function'
        ? normalizeCartPropertiesNow()
        : window.__mimeoCartPropertyNormalizationPromise;

      window.__mimeoCartPropertyNormalizationPromise = normalizationPromise;

      var waitForNormalization =
        normalizationPromise && typeof normalizationPromise.then === 'function'
          ? normalizationPromise.catch(function(error) {
              console.error('[MediaClip] Cart normalization failed before checkout validation:', error);
            })
          : Promise.resolve();

      waitForNormalization
        .then(function() {
          return waitForCartApiMutationQueue();
        })
        .then(function() {
          if (!cartApi || typeof cartApi.saveCart !== 'function') {
            return Promise.resolve();
          }

          return cartApi.saveCart().catch(function(error) {
            console.error('[MediaClip] Cart save failed before checkout validation:', error);
          });
        })
        .then(function() {
          return cartApi.executeMediaclipValidationAndFixCart({ skipRerender: false });
        })
        .then(function (validationResult) {
          if (validationResult && validationResult.result === 'Error') {
            setCartErrorMessage('An error occurred while validating your cart. Please try again.');
            return;
          }

          if (validationResult && validationResult.result === 'Fixed') {
            setCartErrorMessage('Your cart has been updated. Please review it and try checkout again.');
            return saveCartAndRefreshHandlers();
          }

          var resultCode = validationResult && validationResult.result;

          if (resultCode !== 'Ok') {
            // Anything other than the three known codes (including a missing
            // result) used to fall off the end of this chain: the button was
            // re-enabled, nothing was submitted, and no message was shown, so
            // the shopper clicked checkout and nothing happened. Surface it.
            console.error('[MediaClip] Unexpected cart validation result:', validationResult);
            setCartErrorMessage('An error occurred while validating your cart. Please try again.');
            return;
          }

          var finalNormalize = typeof window.__mimeoNormalizeCartProperties === 'function'
            ? window.__mimeoNormalizeCartProperties()
            : Promise.resolve();

          return Promise.resolve(finalNormalize)
            .catch(function(error) {
              console.error('[MediaClip] Final cart property normalization failed before submit:', error);
            })
            .then(function() {
              return waitForQueuedCartMutations();
            })
            .then(function() {
              cartForm && cartForm.submit();
            });
        })
        .catch(function (error) {
          console.error('[MediaClip] Checkout validation failed:', error);
          setCartErrorMessage('An error occurred while validating your cart. Please try again.');
        })
        .finally(function () {
          setCheckoutEnabled(true);
        });
    });
  }

  function rebindUiHandlers() {
    applyMediaclipCartModeUi();
    checkoutButton = document.getElementById('checkout');
    cartForm = document.getElementById('cart');

    setupQuantityChangeButtons();
    setupRemoveButtons();
    setupCheckoutValidation();
    setCheckoutEnabled(cartApiMutationsInFlight === 0);
  }

  function initMediaclipCustomCart() {
    if (!isCartPage()) return;
    if (!shouldUseMediaclipCartFlow()) {
      window.__mimeoMediaclipCartActive = false;
      return;
    }

    checkoutButton = document.getElementById('checkout');
    cartForm = document.getElementById('cart');

    if (!checkoutButton || !cartForm) return;

    window.__mimeoMediaclipCartActive = false;
    delete window.__mimeoValidateMediaclipCart;
    setCheckoutEnabled(false);

    var mediaclipShopify = window.mediaclipShopify;
    if (!mediaclipShopify || typeof mediaclipShopify.getCartApi !== 'function') {
      console.warn('[MediaClip] Cart API is unavailable; falling back to native cart behavior.');
      restoreNativeCartModeUi();
      setCheckoutEnabled(true);
      return;
    }

    // Wait for IIFE 1's normalization pipeline to finish before initializing the
    // MediaClip cart API. normalizeCurrentCartOnPageLoad() makes sequential
    // /cart/update.js and /cart/change.js REST mutations. cartApi.init() makes
    // concurrent GraphQL cart mutations internally. Running both at the same time
    // causes Shopify to return "Cart conflicted with another request".
    // Bounded: checkout was disabled just above and is only re-enabled further
    // down this chain, so an unbounded wait here leaves the cart page with a
    // permanently dead checkout button and no error shown.
    var normalizationPromise = window.__mimeoCartPropertyNormalizationPromise || Promise.resolve();
    withFailOpenTimeout(
      normalizationPromise,
      CART_INIT_NORMALIZATION_TIMEOUT_MS,
      'Cart normalization before MediaClip cart init'
    ).then(function () {
      cartApi = mediaclipShopify.getCartApi();
      window.__mimeoValidateMediaclipCart = validateMediaclipCartForProjectNavigation;

      cartApi.init(CART_SECTIONS, null, function (updatedSections) {
        rebindUiHandlers();
      })
        .then(function () {
          window.__mimeoMediaclipCartActive = true;
          rebindUiHandlers();
          refreshProjectThumbnails();
          return refreshCartSectionsFromServer();
        })
        .catch(function (error) {
          console.error('[MediaClip] Failed to initialize custom cart integration:', error);
          window.__mimeoMediaclipCartActive = false;
          delete window.__mimeoValidateMediaclipCart;
          restoreNativeCartModeUi();
          setCheckoutEnabled(true);
        });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initMediaclipCustomCart);
  } else {
    initMediaclipCustomCart();
  }
})();
